// lib/brands/brandOrigin.ts
//
// How a brand entered the referential. Brand carries no createdAt, so the historical waves were
// reconstructed on 2026-09-27 from the timestamp inside each cuid and the scripts that ran that day.
// `null` means no known origin (created after the backfill by a path that does not set it yet).

export const BRAND_ORIGINS = {
  EARLY_EXTRACTION: 'Early extraction (Nov 2025)',
  AI_MEMORY: 'AI memory, Gemini list (16 Dec 2025)',
  FRANCHISE_DIRECTORY: 'Franchise directory scrape (Jan 2026)',
  COMPANY_REGISTRY: 'Company registry discovery (Jan 2026)',
  MALL_TENANTS: 'Shopping-centre tenant lists (May 2026)',
  OPENSTREETMAP: 'OpenStreetMap import (24–27 May 2026)',
  ACQUISITION_AGENTS: 'Acquisition agents (Jul–Aug 2026)',
  OTHER: 'Other / one-off',
} as const;

export type BrandOrigin = keyof typeof BRAND_ORIGINS;

export const brandOriginLabel = (origin: string | null | undefined): string =>
  origin && origin in BRAND_ORIGINS ? BRAND_ORIGINS[origin as BrandOrigin] : 'Unknown';

/** The creation instant encoded in a cuid (`c` + base-36 milliseconds). */
export const cuidCreatedAt = (id: string): Date | null => {
  if (!/^c[0-9a-z]{24}$/.test(id)) return null;
  const date = new Date(parseInt(id.slice(1, 9), 36));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Maps a brand's creation day onto the import that produced it. */
export const inferBrandOrigin = (id: string): BrandOrigin => {
  const created = cuidCreatedAt(id);
  if (!created) return 'OTHER';
  const day = created.toISOString().slice(0, 10);
  if (day < '2025-12-01') return 'EARLY_EXTRACTION';
  if (day === '2025-12-16') return 'AI_MEMORY';
  if (day >= '2026-01-08' && day <= '2026-01-09') return 'FRANCHISE_DIRECTORY';
  if (day >= '2026-01-10' && day <= '2026-01-31') return 'COMPANY_REGISTRY';
  if (day >= '2026-05-06' && day <= '2026-05-20') return 'MALL_TENANTS';
  if (day >= '2026-05-21' && day <= '2026-05-31') return 'OPENSTREETMAP';
  if (day >= '2026-07-01' && day <= '2026-08-31') return 'ACQUISITION_AGENTS';
  return 'OTHER';
};
